/**
 * Queue (§7.3, right region). Four groups, deliberately not one list:
 *  - scheduled, in fire order
 *  - awaiting owner approval, visually distinct with the control inline
 *  - recently sent, with links to the artifact
 *  - failed, with the adapter's own reason — except moderation
 *    blocks, which are final and say so
 *
 * Scheduled times render in the bot's timezone with the viewer's local time
 * alongside.
 */

import { useState } from 'react';
import { EMPTY_STATES } from '@pm/shared';
import { apiFetch } from '../../api/client';
import { toProblem, type Problem } from '../../api/problem';
import { useApi } from '../../hooks/useApi';
import type { Action, Bot, Page } from '../../api/types';
import { BotTime } from '../../components/TimeDisplay';
import { EmptyState } from '../../components/EmptyState';
import { ErrorView } from '../../components/ErrorView';
import { Badge, Card, Loading } from '../../components/ui';
import { humanize } from '../../lib/format';
import { routes } from '../../api/routes';

function group(actions: Action[]) {
  return {
    approving: actions.filter((a) => a.awaiting_owner_approval),
    scheduled: actions
      .filter((a) => !a.awaiting_owner_approval && (a.status === 'scheduled' || a.status === 'sending'))
      .sort((a, b) => (a.scheduled_at ?? '').localeCompare(b.scheduled_at ?? '')),
    sent: actions.filter((a) => a.status === 'sent').slice(0, 10),
    failed: actions.filter((a) => a.status === 'failed' || a.status === 'blocked'),
  };
}

export function Queue({ bot, refreshToken }: { bot: Bot; refreshToken: number }) {
  const [problem, setProblem] = useState<Problem | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const q = useApi<Page<Action>>(routes.content.actions(bot.id), {
    query: { limit: 50, _t: refreshToken },
    pollMs: 30_000,
  });

  const act = async (path: string, body: Record<string, unknown>, id: string) => {
    setBusy(id);
    setProblem(null);
    try {
      await apiFetch(path, { method: 'POST', body });
      q.reload();
    } catch (e) {
      setProblem(toProblem(e));
    } finally {
      setBusy(null);
    }
  };

  if (q.initialLoading) return <Loading label="Loading the queue" />;
  if (q.problem) return <ErrorView problem={q.problem} onRetry={q.reload} />;

  const actions = q.data?.data ?? [];
  const g = group(actions);
  const nothingAtAll = actions.length === 0;

  return (
    <div className="queue">
      {problem ? <ErrorView problem={problem} /> : null}

      {nothingAtAll ? (
        <Card title="Queue">
          <EmptyState
            heading={EMPTY_STATES.consoleNoActions.heading}
            body={EMPTY_STATES.consoleNoActions.body}
          />
        </Card>
      ) : null}

      {g.approving.length > 0 ? (
        <Card title="Waiting for approval" tone="warn">
          <ul className="queue-list queue-approving">
            {g.approving.map((a) => (
              <li key={a.id} className="queue-item queue-item-approving">
                <div className="queue-item-main">
                  <Badge tone="warn">Needs approval</Badge>
                  <p className="queue-body">{a.body || `(${a.type})`}</p>
                  <p className="muted">
                    From @{a.actor_handle} ({a.actor_role}) · {a.platform} @{a.social_account_handle}
                  </p>
                  <p className="muted">
                    <BotTime iso={a.scheduled_at ?? a.created_at} timeZone={bot.timezone} />
                  </p>
                </div>
                {/* Server says whether this actor may resolve it (§7.4 rule 1). */}
                {a.can_approve ? (
                  <div className="queue-item-actions">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={busy === a.id}
                      onClick={() => void act(routes.content.actionApproval(a.id), { approve: true }, a.id)}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={busy === a.id}
                      onClick={() => void act(routes.content.actionApproval(a.id), { approve: false }, a.id)}
                    >
                      Decline
                    </button>
                  </div>
                ) : (
                  <p className="muted queue-item-actions">The owner has to approve this.</p>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {g.scheduled.length > 0 ? (
        <Card title="Scheduled">
          <ul className="queue-list">
            {g.scheduled.map((a) => (
              <li key={a.id} className="queue-item">
                <div className="queue-item-main">
                  <Badge tone="info">{a.status}</Badge>
                  <p className="queue-body">{a.body || `(${a.type})`}</p>
                  <p className="muted">
                    <BotTime iso={a.scheduled_at} timeZone={bot.timezone} />
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {g.sent.length > 0 ? (
        <Card title="Recently sent">
          <ul className="queue-list">
            {g.sent.map((a) => (
              <li key={a.id} className="queue-item">
                <div className="queue-item-main">
                  <Badge tone="ok">sent</Badge>
                  <p className="queue-body">{a.body || `(${a.type})`}</p>
                  <p className="muted">
                    <BotTime iso={a.executed_at} timeZone={bot.timezone} />
                    {a.external_url ? (
                      <>
                        {' · '}
                        <a href={a.external_url} target="_blank" rel="noreferrer noopener">
                          View on {a.platform}
                        </a>
                      </>
                    ) : null}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {g.failed.length > 0 ? (
        <Card title="Failed" tone="danger">
          <ul className="queue-list">
            {g.failed.map((a) => (
              <li key={a.id} className="queue-item">
                <div className="queue-item-main">
                  <Badge tone="bad">{a.status}</Badge>
                  <p className="queue-body">{a.body || `(${a.type})`}</p>
                  {a.status === 'blocked' ? (
                    <p className="failure-reason">
                      Blocked by the content policy
                      {a.moderation_category ? ` — ${humanize(a.moderation_category)}` : ''}. A block
                      is final; rewrite it and compose a new one.
                    </p>
                  ) : (
                    <p className="failure-reason">
                      {a.failure_reason ?? 'The adapter gave no reason.'}
                    </p>
                  )}
                </div>
                {/*
                  Moderation blocks are never retryable (§7.3); the server decides.
                  TODO(api): §10.4 has no retry route — PATCH /actions/{id} edits a
                  scheduled action and does not re-run a failed one — so a failed
                  action says why it failed and is composed again rather than
                  offering a button with nothing behind it. It needs
                  POST /api/v1/actions/{id}/retry.
                */}
                {a.retryable ? (
                  <p className="muted">
                    This one can be tried again. Compose it again from the same text; there is no
                    one-click retry yet.
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
